import { createRouter, createWebHistory } from 'vue-router';

// Lazy loading : Ne charge les composants que lorsque l'utilisateur visite la page
// C'est vital pour avoir un score de performance de 100/100 sur Google Lighthouse
const HomePage = () => import('@/views/HomePage.vue');
const MenuPage = () => import('@/views/MenuPage.vue');
const ContactPage = () => import('@/views/ContactPage.vue');
const AboutPage = () => import('@/views/AboutPage.vue');
const PrivacyPolicyPage = () => import('@/views/PrivacyPolicyPage.vue');
const LegalNoticePage = () => import('@/views/LegalNoticePage.vue');

const routes = [
  {
    path: '/',
    name: 'HomePage',
    component: HomePage,
    meta: {
      title: 'Restaurant Pizzeria Treviglio | Pizzas artisanales à Flawinne (Namur)',
      description: 'Découvrez la Pizzeria Treviglio à Flawinne : pizzas artisanales au feu de bois et plats italiens authentiques. Sur place, à emporter ou en livraison.',
      ogImage: 'https://pizzeriatreviglio.be/img01.jpg',
    }
  },
  {
    path: '/menu',
    name: 'MenuPage',
    component: MenuPage,
    meta: {
      title: 'Notre Carte & Menu | Pizzeria Treviglio Namur',
      description: 'Consultez la carte de la Pizzeria Treviglio : grand choix de pizzas cuites au four à bois, pâtes fraîches, salades et desserts italiens traditionnels.',
      ogImage: 'https://pizzeriatreviglio.be/img04.jpg', // Vous pouvez mettre une photo de pizza ici
    }
  },
  {
    path: '/about',
    name: 'AboutPage',
    component: AboutPage,
    meta: {
      title: 'À propos de nous | L\'histoire de la Pizzeria Treviglio',
      description: 'Rencontrez l\'équipe de la Pizzeria Treviglio. Découvrez notre passion pour la gastronomie italienne et l\'histoire de notre restaurant à Flawinne.',
      ogImage: 'https://pizzeriatreviglio.be/interior.webp',
    }
  },
  {
    path: '/contact',
    name: 'ContactPage',
    component: ContactPage,
    meta: {
      title: 'Contact & Réservation | Pizzeria Treviglio Flawinne',
      description: 'Réservez votre table ou commandez vos pizzas à emporter. Retrouvez nos horaires d\'ouverture, notre adresse et notre numéro de téléphone.',
      ogImage: 'https://pizzeriatreviglio.be/img01.jpg',
    }
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicyPage',
    component: PrivacyPolicyPage,
    meta: {
      title: 'Politique de Confidentialité | Pizzeria Treviglio',
      description: 'Consultez notre politique de confidentialité et la gestion de vos données personnelles.',
      noIndex: true
    }
  },
  {
    path: '/legal-notice',
    name: 'LegalNoticePage',
    component: LegalNoticePage,
    meta: {
      title: 'Mentions Légales | Pizzeria Treviglio',
      description: 'Mentions légales et informations juridiques du Restaurant Pizzeria Treviglio.',
      noIndex: true
    }
  },
  // Redirection 404 : Si l'utilisateur tape une mauvaise URL, on le renvoie à l'accueil
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
];

const router = createRouter({
  // Utilisation de l'historique HTML5 (sans le # dans l'URL)
  history: createWebHistory(process.env.BASE_URL),
  routes,
  
  // Gestion du comportement de défilement fluide
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition; // Si l'utilisateur clique sur "Retour", on le remet où il était
    } else {
      // Gestion des ancres internes (ex: /menu#pizzas)
      if (to.hash) {
        return {
          el: to.hash,
          behavior: 'smooth',
          top: 150 // Compense la hauteur du header (navbar fixed) pour ne pas cacher le titre
        };
      }
      // Par défaut, on remonte en haut de la page
      return { top: 0, behavior: 'smooth' };
    }
  },
});

/**
 * Navigation Guard (S'exécute avant chaque changement de page)
 * Gère dynamiquement toutes les balises SEO (Title, Meta, OpenGraph, Robots)
 */
router.beforeEach((to, from, next) => {
  // --- 1. Mise à jour de la balise <title> ---
  const defaultTitle = 'Pizzeria Treviglio | Restaurant Italien à Namur';
  document.title = to.meta.title || defaultTitle;

  // Fonction utilitaire pour créer ou mettre à jour une balise meta
  const updateOrCreateMetaTag = (attributeName, attributeValue, content) => {
    let tag = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute(attributeName, attributeValue);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  };

  // --- 2. Mise à jour de la balise <meta name="description"> ---
  const description = to.meta.description || 'Restaurant italien et pizzeria artisanale à Flawinne (Namur). Pizzas au feu de bois et plats faits maison.';
  updateOrCreateMetaTag('name', 'description', description);

  // --- 3. Balises Open Graph (Facebook, LinkedIn, WhatsApp) ---
  const currentUrl = `https://pizzeriatreviglio.be${to.fullPath}`;
  const ogImage = to.meta.ogImage || 'https://pizzeriatreviglio.be/img01.jpg'; // Image par défaut si non spécifiée
  
  updateOrCreateMetaTag('property', 'og:title', document.title);
  updateOrCreateMetaTag('property', 'og:description', description);
  updateOrCreateMetaTag('property', 'og:url', currentUrl);
  updateOrCreateMetaTag('property', 'og:image', ogImage);
  updateOrCreateMetaTag('property', 'og:type', 'website');
  updateOrCreateMetaTag('property', 'og:locale', 'fr_BE');

  // --- 4. Balises Twitter Cards (X) ---
  updateOrCreateMetaTag('name', 'twitter:card', 'summary_large_image');
  updateOrCreateMetaTag('name', 'twitter:title', document.title);
  updateOrCreateMetaTag('name', 'twitter:description', description);
  updateOrCreateMetaTag('name', 'twitter:image', ogImage);

  // --- 5. Gestion de l'indexation (Robots) ---
  if (to.meta.noIndex) {
    updateOrCreateMetaTag('name', 'robots', 'noindex, nofollow');
  } else {
    updateOrCreateMetaTag('name', 'robots', 'index, follow');
  }

  // --- 6. URL Canonique (Évite le Duplicate Content) ---
  let linkCanonical = document.querySelector('link[rel="canonical"]');
  if (!linkCanonical) {
    linkCanonical = document.createElement('link');
    linkCanonical.setAttribute('rel', 'canonical');
    document.head.appendChild(linkCanonical);
  }
  // Supprime les paramètres d'URL (comme ?utm_source=...) pour l'URL canonique
  const cleanUrl = currentUrl.split('?')[0]; 
  linkCanonical.setAttribute('href', cleanUrl);

  next(); // On autorise le changement de route
});

export default router;
